// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ALL_REQUEST_TYPES } from '@vn-grc/shared';
import type { PublicIntakePayload } from '@vn-grc/shared';
import { apiClient } from '../../services/api-client.js';
import { useLocale } from '../../hooks/useLocale.js';
import { useInstalledLanguages } from '../../hooks/useInstalledLanguages.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';

/**
 * Public intake form for data subject rights requests.
 * Accessible without authentication.
 *
 * Phase 9 extension: supports ?key=<intake_key> query param for
 * deterministic org routing. When a key is provided, the form loads
 * the intake config to get org context and enabled request types.
 *
 * See: docs/product/phases/phase-1/sprint-1.0.0.md - Epic A
 */
export function IntakeFormPage() {
  usePageTitle('Intake Form');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intakeKey = searchParams.get('key') || undefined;
  const { locale, setLocale, t } = useLocale();
  const { languages } = useInstalledLanguages();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [allowedRequestTypes, setAllowedRequestTypes] = useState<string[] | null>(null);

  /** Extract short language code from locale (e.g. 'sv-SE' → 'sv'). */
  const shortLang = locale.split('-')[0];

  const [form, setForm] = useState<
    PublicIntakePayload & {
      customer_reference?: string;
      country?: string;
      preferred_response_channel?: string;
    }
  >({
    request_type: '' as any,
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    customer_reference: '',
    country: '',
    preferred_response_channel: 'email',
    description: '',
    preferred_language: shortLang,
    consent_to_processing: false,
  });

  // Load intake config: by key if provided, otherwise auto-resolve from backend
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = intakeKey
          ? await apiClient.getPublicIntakeConfig(intakeKey)
          : await apiClient.getDefaultPublicIntakeConfig();
        if (config) {
          setOrgName(config.org_name);
          if (config.enabled_request_types?.length) {
            setAllowedRequestTypes(config.enabled_request_types);
          }
          if (config.default_language) {
            setForm((prev) => ({ ...prev, preferred_language: config.default_language }));
            // Only switch the UI locale when navigating via an explicit intake key.
            // Auto-resolve should not override the user's current browser locale.
            if (intakeKey) {
              const matchedLocale = languages.find((o) =>
                o.value.toLowerCase().startsWith(config.default_language.toLowerCase() + '-'),
              );
              if (matchedLocale) setLocale(matchedLocale.value);
            }
          }
        }
      } catch {
        if (intakeKey) {
          setConfigError(
            ((t.intake as Record<string, unknown>).config_load_error as string) ??
              'Unable to load intake configuration. Please check the URL.',
          );
        }
      } finally {
        setConfigLoading(false);
      }
    };
    loadConfig();
  }, [intakeKey]);

  const handleChange = (field: keyof PublicIntakePayload, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const result = await apiClient.submitIntake(form, intakeKey);
      navigate('/rights-request/confirmation', {
        state: { referenceNumber: result.reference_number, language: form.preferred_language },
      });
    } catch (err: any) {
      if (err.details?.issues) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of err.details.issues) {
          fieldErrors[issue.path] = issue.message;
        }
        setErrors(fieldErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use allowed request types from config, or all types if no config
  const displayRequestTypes = allowedRequestTypes || ALL_REQUEST_TYPES;

  const intakeT = t.intake as Record<string, unknown>;
  const intakeChannels = (intakeT.channels ?? {}) as Record<string, string>;

  if (configLoading) {
    return (
      <div className="intake-form-page">
        <div className="intake-form-container">
          <p>{t.common.loading}</p>
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="intake-form-page">
        <div className="intake-form-container">
          <h1>{(intakeT.error_title as string) ?? 'Error'}</h1>
          <p>{configError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-form-page">
      <div className="intake-form-container">
        {/* Language selector */}
        <div className="language-selector">
          <select
            value={locale}
            onChange={(e) => {
              const newLocale = e.target.value;
              setLocale(newLocale);
              handleChange('preferred_language', newLocale.split('-')[0]);
            }}
          >
            {languages.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <h1>{t.intake.title}</h1>
        {orgName && (
          <p className="intake-org-name">
            {(intakeT.organization_label as string) ?? 'Organization'}: <strong>{orgName}</strong>
          </p>
        )}
        <p>{t.intake.subtitle}</p>

        <form onSubmit={handleSubmit}>
          {/* Full Name */}
          <div className="form-field">
            <label htmlFor="requester_name">{t.intake.fields.full_name} *</label>
            <input
              id="requester_name"
              type="text"
              value={form.requester_name}
              onChange={(e) => handleChange('requester_name', e.target.value)}
              required
            />
            {errors.requester_name && <span className="error">{errors.requester_name}</span>}
          </div>

          {/* Email */}
          <div className="form-field">
            <label htmlFor="requester_email">{t.intake.fields.email} *</label>
            <input
              id="requester_email"
              type="email"
              value={form.requester_email}
              onChange={(e) => handleChange('requester_email', e.target.value)}
              required
            />
            {errors.requester_email && <span className="error">{errors.requester_email}</span>}
          </div>

          {/* Phone */}
          <div className="form-field">
            <label htmlFor="requester_phone">{t.intake.fields.phone}</label>
            <input
              id="requester_phone"
              type="tel"
              value={form.requester_phone ?? ''}
              onChange={(e) => handleChange('requester_phone', e.target.value || null)}
            />
          </div>

          {/* Customer Reference */}
          <div className="form-field">
            <label htmlFor="customer_reference">
              {(intakeT.customer_reference as string) ?? 'Customer Reference Number'}
            </label>
            <input
              id="customer_reference"
              type="text"
              value={form.customer_reference ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, customer_reference: e.target.value }))}
              maxLength={100}
            />
          </div>

          {/* Country / Jurisdiction */}
          <div className="form-field">
            <label htmlFor="country">
              {(intakeT.country as string) ?? 'Country / Jurisdiction'}
            </label>
            <input
              id="country"
              type="text"
              value={form.country ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))}
              maxLength={100}
            />
          </div>

          {/* Preferred Response Channel */}
          <div className="form-field">
            <label htmlFor="preferred_response_channel">
              {(intakeT.preferred_channel as string) ?? 'Preferred Response Channel'}
            </label>
            <select
              id="preferred_response_channel"
              value={form.preferred_response_channel ?? 'email'}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, preferred_response_channel: e.target.value }))
              }
            >
              <option value="email">{intakeChannels.email ?? 'Email'}</option>
              <option value="postal">{intakeChannels.postal ?? 'Postal'}</option>
              <option value="phone">{intakeChannels.phone ?? 'Phone'}</option>
              <option value="portal">{intakeChannels.portal ?? 'Portal'}</option>
            </select>
          </div>

          {/* Request Type */}
          <div className="form-field">
            <label htmlFor="request_type">{t.intake.fields.request_type} *</label>
            <select
              id="request_type"
              value={form.request_type}
              onChange={(e) => handleChange('request_type', e.target.value)}
              required
            >
              <option value="">--</option>
              {displayRequestTypes.map((type) => (
                <option key={type} value={type}>
                  {(t.request_types as Record<string, string>)[type] || type}
                </option>
              ))}
            </select>
            {errors.request_type && <span className="error">{errors.request_type}</span>}
          </div>

          {/* Description */}
          <div className="form-field">
            <label htmlFor="description">{t.intake.fields.description} *</label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={5}
              maxLength={5000}
              required
            />
            {errors.description && <span className="error">{errors.description}</span>}
          </div>

          {/* Consent */}
          <div className="form-field form-field--checkbox">
            <label>
              <input
                type="checkbox"
                checked={form.consent_to_processing}
                onChange={(e) => handleChange('consent_to_processing', e.target.checked)}
                required
              />
              {t.intake.fields.consent} *
            </label>
            {errors.consent_to_processing && (
              <span className="error">{errors.consent_to_processing}</span>
            )}
          </div>

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t.common.loading : t.intake.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
